import { ClientDto } from '@act/shared/data-transfer-objects';
import {
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { useAppSelector } from 'redux/store-hooks';
import { clientsRequests } from '../requests/clients.requests';
import { ClientState } from '../types/clientTypes';
import { createApiThunk } from '../utils/api-thunk';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const getClientById = createApiThunk(clientsRequests.getClient);
export const getAllClients = createApiThunk(clientsRequests.getAllClients);
export const updateClient = createApiThunk(clientsRequests.updateClient);
export const createClient = createApiThunk(clientsRequests.createClient);

export const clientUpdatedSuccess = createAction<ClientDto>(
  'clientUpdatedSuccess'
);
export const clientCreatedSuccess = createAction<ClientDto>(
  'clientCreatedSuccess'
);
// INITIAL STATE ===============================================================
const clientEntityAdapter = createEntityAdapter<ClientDto>();

const selectAllClientsFromState = clientEntityAdapter.getSelectors().selectAll;

const getActiveClients = (clients: ClientState['clients']): ClientDto[] => {
  return selectAllClientsFromState(clients).filter(client => {
    return (
      !client.name.toLowerCase().includes('test') &&
      !client.name.toLowerCase().includes('unassignment') &&
      client.isActive === true
    );
  });
};

const initialState: ClientState = {
  status: 'initial',
  clients: clientEntityAdapter.getInitialState(),
  activeClients: [],
};

// SLICE =======================================================================
export const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getClientById.pending, state => {
        state.status = 'loading';
      })
      .addCase(
        getClientById.fulfilled,
        (state, action: PayloadAction<ClientDto>) => {
          clientEntityAdapter.upsertOne(state.clients, action.payload);
          state.activeClients = getActiveClients(state.clients);
          state.status = 'success';
        }
      )
      .addCase(getClientById.rejected, state => {
        state.status = 'failed';
      })
      .addCase(getAllClients.pending, state => {
        clientEntityAdapter.removeAll(state.clients);
        state.status = 'loading';
      })
      .addCase(getAllClients.fulfilled, (state, action) => {
        clientEntityAdapter.setAll(state.clients, action.payload);
        state.activeClients = getActiveClients(state.clients);
        state.status = 'success';
      })
      .addCase(getAllClients.rejected, state => {
        state.status = 'failed';
      })
      .addCase(
        clientUpdatedSuccess,
        (state, action: PayloadAction<ClientDto>) => {
          clientEntityAdapter.upsertOne(state.clients, action.payload);
          state.activeClients = getActiveClients(state.clients);
          state.status = 'success';
        }
      )
      .addCase(
        clientCreatedSuccess,
        (state, action: PayloadAction<ClientDto>) => {
          clientEntityAdapter.upsertOne(state.clients, action.payload);
          state.activeClients = getActiveClients(state.clients);
          state.status = 'success';
        }
      );
  },
});

// REDUCER =====================================================================
export const clientsReducer = clientsSlice.reducer;

// SELECTORS ===================================================================
const selectClientsSliceState = state =>
  (state as { clients: typeof initialState })?.clients;

export const selectClients = createSelector(
  selectClientsSliceState,
  clientState => clientState.clients
);

export const selectClientStatus = createSelector(
  selectClientsSliceState,
  clientState => clientState.status
);

export const selectAllClients = createSelector(
  selectClientsSliceState,
  clientState => clientState.clients
);

export const selectClientById = (id: number) =>
  createSelector(selectClients, client => {
    return client.entities[id];
  });

export const selectActiveClients = createSelector(
  selectClientsSliceState,
  clientState => clientState.activeClients
);

// EXPORT HELPER ===============================================================
export const clientAppSelectors = {
  useSelectClientById: id => useAppSelector(selectClientById(id)),
  useSelectAllClients: () => useAppSelector(selectAllClients),
  useSelectAllClientsStatus: () => useAppSelector(selectClientStatus),
  useSelectActiveClients: () => useAppSelector(selectActiveClients),
};

// EFFECTS =====================================================================

// WEBSOCKET ACTIONS ===========================================================
